"use client"

import { useEffect, useState } from "react"
import { db } from "../firebase"
import { collection, getDocs } from "firebase/firestore"
import {
  FaUserTie,
  FaEnvelope,
  FaSpinner,
  FaExclamationTriangle,
  FaFlask,
  FaIndustry,
  FaAward,
  FaRocket,
  FaQuoteLeft,
  FaEye,
  FaBullseye,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaGlobe,
} from "react-icons/fa"

const Department = () => {
  const [facultyList, setFacultyList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedFaculty, setExpandedFaculty] = useState({})
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        setLoading(true)
        const snapshot = await getDocs(collection(db, "faculty"))
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

        // Sort by order field, fallback to name if order doesn't exist
        const sortedData = data.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order
          }
          return a.name.localeCompare(b.name)
        })

        setFacultyList(sortedData)
      } catch (error) {
        console.error("Failed to load faculty data", error)
        setError("Failed to load faculty information. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchFaculty()
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const toggleFaculty = (index) => {
    setExpandedFaculty((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const highlights = [
    {
      icon: FaAward,
      title: "93% PhD Faculty",
      description: "Highly qualified and experienced teaching staff",
      color: "from-blue-500 to-blue-700",
    },
    {
      icon: FaRocket,
      title: "25% Study Abroad",
      description: "Students pursue higher studies internationally",
      color: "from-green-500 to-green-700",
    },
    {
      icon: FaIndustry,
      title: "Top Placements",
      description: "Students placed in companies like GE, Schneider, Akzo Nobel",
      color: "from-purple-500 to-purple-700",
    },
    {
      icon: FaFlask,
      title: "State-of-art Labs",
      description: "Excellent facilities and instrumentation equipment",
      color: "from-orange-500 to-orange-700",
    },
  ]

  const industries = [
    "Petroleum & Petrochemicals",
    "Energy & Power Generation",
    "Pharmaceuticals & Biotechnology",
    "Nanotechnology",
    "Environmental Engineering",
    "Defence & Mining",
    "Steel & Aluminium",
    "Food & Agro-based Technologies",
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 dark:bg-green-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 dark:bg-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-60 h-60 bg-purple-200 dark:bg-purple-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Hero Section with Parallax */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-green-600 via-blue-600 to-purple-700"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Animated Particles */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            ></div>
          ))}
        </div>

        <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-8 animate-bounce-slow">
            <FaFlask className="w-12 h-12" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up">
            About <span className="text-green-300 animate-pulse">Department</span>
          </h1>
          <div className="w-24 h-1 bg-green-300 mx-auto mb-8 animate-expand"></div>
          <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-500">
            Chemical Engineering Department - Amrita Vishwa Vidyapeetham
          </p>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Highlights Section */}
      <div className="relative -mt-32 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                data-animate
                id={`highlight-${index}`}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 text-center transform transition-all duration-700 hover:scale-105 hover:shadow-3xl ${
                  visibleSections.has(`highlight-${index}`) ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${highlight.color} rounded-full mb-4 shadow-lg`}
                >
                  <highlight.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{highlight.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Introduction Section */}
        <div
          data-animate
          id="intro"
          className={`relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 mb-16 overflow-hidden transform transition-all duration-700 ${
            visibleSections.has("intro") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-green-200 to-blue-200 dark:from-green-800 dark:to-blue-800 rounded-full opacity-20"></div>

          <div className="relative flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                <FaQuoteLeft className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-6">
                Leading Transformation
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                India is undergoing significant transformation and realignment on a global scale across various sectors,
                including infrastructure, industries, the environment, energy, and the economy. Chemical engineering is
                one of the disciplines that is leading and shaping this transformation at the forefront.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Chemical engineers play a vital role in such diverse industries as petroleum and petrochemicals, energy,
                power generation, including ultra-mega power plants, coal-bed methane, combined gas cycle plants,
                nuclear power, defence, mining, and minerals, including steel and aluminium, pharmaceuticals and
                biotechnology, nanotechnology, cement, fertilizers, textiles, leather, food and agro-based technologies,
                polymers, and environmental engineering.
              </p>
            </div>
          </div>
        </div>

        {/* Industries Section */}
        <div
          data-animate
          id="industries"
          className={`mb-16 transform transition-all duration-700 ${
            visibleSections.has("industries") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Industry Applications
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaIndustry className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{industry}</h3>
              </div>
            ))}
          </div>
        </div>

        {/* Program Description */}
        <div
          data-animate
          id="program"
          className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 mb-16 transform transition-all duration-700 ${
            visibleSections.has("program") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Our Program Excellence
            </h2>
          </div>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            In this context, the demand for qualified professional chemical engineers to propel the growth of the
            industry and shape the contours of the Indian economy is on the rise. The undergraduate (B. Tech) program
            offered in Chemical Engineering at Amrita Vishwa Vidyapeetham has a curriculum of global standards
            emphasizing the fundamentals, breadth of the discipline, and the necessary flexibility in being able to
            apply the fundamentals in widely varying contexts.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            It simultaneously trains the students in specialized elective streams such as materials science,
            nanotechnology, pharmaceuticals and petrochemicals, and petroleum refineries. With 93% of the faculty having
            a PhD degree and another 7% actively pursuing their PhD, the students get to learn from well-qualified and
            experienced staff. The students learn hands-on engineering skills in excellent labs, workshops, and on
            computers. They also use the excellent library facilities and computing facilities on campus and have access
            to the state-of-the-art instrumentation equipment at the Amrita Centre for Nanoscience.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Mission */}
          <div
            data-animate
            id="mission"
            className={`bg-gradient-to-br from-green-600 to-green-700 rounded-3xl p-10 text-white transform transition-all duration-700 ${
              visibleSections.has("mission") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FaBullseye className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-bold">Mission</h2>
            </div>
            <p className="text-green-100 mb-6">
              The department strives for a passionate and committed drive towards continuous improvement in:
            </p>
            <ul className="space-y-3 text-green-100">
              <li className="flex items-start space-x-3">
                <FaStar className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>The delivery, standards, and currency of education</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaStar className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>Administration efficiency</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaStar className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>
                  Socially beneficial scientific research to create new processes, products, methods, materials, or
                  systems that impact and are beneficial to society
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <FaStar className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>Meeting and exceeding the needs of the stakeholders and Amrita Vishwa Vidyapeetham</span>
              </li>
            </ul>
          </div>

          {/* Vision */}
          <div
            data-animate
            id="vision"
            className={`bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 text-white transform transition-all duration-700 ${
              visibleSections.has("vision") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FaEye className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-bold">Vision</h2>
            </div>
            <p className="text-blue-100 text-lg leading-relaxed">
              To be recognized nationally and internationally for excellence in teaching and research in chemical
              engineering & materials science, integrated with, and practicing, social responsibility and value systems.
            </p>
          </div>
        </div>

        {/* Placement Success */}
        <div
          data-animate
          id="placement"
          className={`bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-10 text-white mb-16 transform transition-all duration-700 ${
            visibleSections.has("placement") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center">
            <FaGlobe className="w-16 h-16 mx-auto mb-6 animate-pulse" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Placement Excellence</h2>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              Amrita School of Engineering and the department have excellent placement records for the graduating
              students. Nearly 25% of our students go abroad every year for higher studies. We have a hard-working and
              enterprising student group. Our students have been placed in top-notch companies like General Electric,
              Schneider India, Akzo Nobel, Apollo Tires, and many more.
            </p>
          </div>
        </div>

        {/* Faculty Section */}
        <div
          data-animate
          id="faculty"
          className={`transform transition-all duration-700 ${
            visibleSections.has("faculty") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Our Faculty
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <FaSpinner className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-600 dark:text-gray-400">Loading faculty information...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md mx-auto">
                <FaExclamationTriangle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Error Loading Faculty</h3>
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          ) : facultyList.length === 0 ? (
            <div className="text-center py-16">
              <FaUserTie className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Faculty Members</h3>
              <p className="text-gray-600 dark:text-gray-400">Faculty information will be displayed here once added.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {facultyList.map((faculty, index) => (
                <div
                  key={faculty.id}
                  className="group bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={faculty.imageUrl || "/placeholder.svg"}
                      alt={faculty.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg"
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <FaUserTie className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{faculty.name}</h3>
                    </div>

                    <div
                      className={`transition-all duration-300 ${
                        expandedFaculty[index] ? "max-h-96" : "max-h-20 overflow-hidden"
                      }`}
                    >
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                        {faculty.description || "No description available."}
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <a
                        href={`mailto:${faculty.email}`}
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
                      >
                        <FaEnvelope className="w-4 h-4" />
                        <span className="text-sm font-medium">Contact</span>
                      </a>

                      {faculty.description && faculty.description.length > 100 && (
                        <button
                          onClick={() => toggleFaculty(index)}
                          className="text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 transition-colors duration-200"
                        >
                          {expandedFaculty[index] ? (
                            <FaChevronUp className="w-5 h-5" />
                          ) : (
                            <FaChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes expand {
          from { width: 0; }
          to { width: 6rem; }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
        .animate-expand { animation: expand 1s ease-out 1s forwards; width: 0; }
        .animation-delay-500 { animation-delay: 0.5s; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .shadow-3xl { box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25); }
      `}</style>
    </div>
  )
}

export default Department
